import { useEffect, useState, useCallback, useRef } from "react";
import api, { apiErrorMessage } from "@/lib/api";
import {
  composeWorkspaceName,
  normalizeWorkspacePart,
  splitWorkspaceName,
} from "@/lib/workspaceName";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { useWorkspace } from "@/context/WorkspaceContext";
import { useCustomization } from "@/context/CustomizationContext";
import ImportDialog from "@/components/common/ImportDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import {
  Sun, Moon, Monitor, Pencil, Trash2, ShieldCheck, Lock, Loader2,
  UserPlus, KeyRound, Copy, RefreshCw, Building2, Upload, X, Plus, AlertTriangle,
} from "lucide-react";

const ROLE_LABELS = {
  super_admin: "Super Admin",
  cxo: "CXO",
  strategy: "Strategy",
  direction: "Direction",
  planning: "Planning",
};

const ROLE_DESCRIPTIONS = {
  super_admin: "Full system control including all workspaces and system settings.",
  cxo:         "CEO / CFO level. Full financial access. Automatically assigned to all workspaces.",
  strategy:    "AVP / VP / SVP level. Full financial access. Assigned to specific workspaces.",
  direction:   "AD / Director / SD level. No Revenue $ visibility. Assigned to specific workspaces.",
  planning:    "AGM / DGM / GM level. No Revenue $ visibility. Assigned to specific workspaces.",
};

const USER_PROFILE_FIELDS = [
  { key: "department", label: "Dept", placeholder: "Ex: RCM Operations" },
  { key: "team", label: "Team", placeholder: "Ex: AAA" },
  { key: "designation", label: "Designation", placeholder: "Ex: Associate General Manager" },
  { key: "scope_of_work", label: "Scope of Work", placeholder: "Ex: Medical Billing" },
  { key: "cost_code", label: "Cost Code", placeholder: "Ex: AAARFIP" },
  { key: "work_mode", label: "Work Mode", placeholder: "Ex: Office" },
  { key: "location", label: "Location", placeholder: "Ex: Vepery" },
  { key: "shift_code", label: "Shift Code", placeholder: "DF1 - 9" },
];

const emptyUserProfile = () => Object.fromEntries(
  USER_PROFILE_FIELDS.map(({ key }) => [key, ""])
);

const userProfileValues = (user = {}) => Object.fromEntries(
  USER_PROFILE_FIELDS.map(({ key }) => [key, user[key] || ""])
);

// Roles that are automatically assigned to all workspaces — picker is hidden for these
const ALL_WS_ROLES = ["super_admin", "cxo"];
const ROLE_LEVELS = { planning: 1, direction: 2, strategy: 3, cxo: 4, super_admin: 5 };
const rolesBelow = (role) => Object.keys(ROLE_LEVELS)
  .filter((candidate) => candidate !== "super_admin" && ROLE_LEVELS[candidate] < ROLE_LEVELS[role]);

const genPassword = () => {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789";
  let p = "";
  for (let i = 0; i < 12; i++) p += chars[Math.floor(Math.random() * chars.length)];
  return p;
};

const copyText = async (text, label) => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  } catch {
    toast.error("Could not copy to clipboard");
  }
};

const emptyWorkspaceForm = () => ({
  open: false,
  teamCode: "",
  clientName: "",
  logo: null,
  setAsDefault: false,
});

const SectionCard = ({ title, subtitle, children, testId }) => (
  <Card data-testid={testId} className="rounded-xl border bg-card shadow-[var(--app-shadow-sm)] p-5 md:p-6">
    <h3 className="text-sm font-semibold text-foreground">{title}</h3>
    {subtitle && <p className="text-xs text-muted-foreground mt-0.5 mb-4">{subtitle}</p>}
    {children}
  </Card>
);

// Per-user workspace access picker: null = all workspaces, [] = none, [ids] = specific.
const WsAccessPicker = ({ workspaces, value, onApply, disabled, testId }) => {
  const [open, setOpen] = useState(false);
  const [all, setAll] = useState(value == null);
  const [ids, setIds] = useState(Array.isArray(value) ? value : []);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setAll(value == null);
      setIds(Array.isArray(value) ? value : []);
    }
  }, [open, value]);

  const summary = value == null
    ? "All workspaces"
    : value.length === 0 ? "No access" : `${value.length} workspace${value.length > 1 ? "s" : ""}`;
  const noAccess = value != null && value.length === 0;

  const toggle = (id) => setIds((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const apply = async () => {
    setSaving(true);
    try {
      await onApply(all, all ? [] : ids);
      setOpen(false);
    } catch {
      // error toast already shown by onApply
    } finally {
      setSaving(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" disabled={disabled} data-testid={testId}
          title={disabled ? "You cannot change your own workspace access" : "Manage workspace access"}
          className={`h-8 rounded-lg text-xs gap-1.5 ${noAccess ? "text-red-600 border-red-200 dark:border-red-900" : ""}`}>
          <Building2 className="h-3.5 w-3.5" /> {summary}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-3" align="end">
        <p className="text-xs font-semibold text-foreground mb-2">Workspace access</p>
        <label className="flex items-center justify-between gap-2 rounded-md border px-2.5 py-2 mb-2 cursor-pointer">
          <span className="text-xs font-medium">All workspaces</span>
          <Switch checked={all} onCheckedChange={setAll} data-testid={`${testId}-all-toggle`} />
        </label>
        {!all && (
          <div className="max-h-44 overflow-y-auto space-y-0.5 mb-2">
            {workspaces.map((w) => (
              <label key={w.id}
                className="flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-muted cursor-pointer">
                <Checkbox checked={ids.includes(w.id)} onCheckedChange={() => toggle(w.id)}
                  data-testid={`${testId}-ws-${w.id}`} />
                <span className="text-xs truncate">{w.name}</span>
              </label>
            ))}
            {ids.length === 0 && (
              <p className="text-[11px] text-red-600 px-2 pt-1">
                No workspaces selected — this user will be blocked from the app.
              </p>
            )}
          </div>
        )}
        <Button size="sm" className="w-full rounded-lg" onClick={apply} disabled={saving}
          data-testid={`${testId}-apply`}>
          {saving ? "Saving..." : "Apply"}
        </Button>
      </PopoverContent>
    </Popover>
  );
};

const THEME_OPTIONS = [
  { value: "light", label: "Light", icon: Sun },
  { value: "dark", label: "Dark", icon: Moon },
  { value: "system", label: "System", icon: Monitor },
];


// ── Logo helpers ──────────────────────────────────────────────────────────

const toBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const isValidLogoFile = (file, toastFn) => {
  if (!file.type.startsWith("image/")) {
    toastFn.error("Please select an image file (PNG, JPG, SVG, …).");
    return false;
  }
  if (file.size > 1_048_576) {
    toastFn.error("Logo must be smaller than 1 MB.");
    return false;
  }
  return true;
};

export default function SettingsPage() {
  const { user, setUser } = useAuth();
  const { theme, setTheme } = useTheme();
  const {
    workspaces, refresh: refreshWorkspaces, current, switchWorkspace,
    defaultWorkspaceId, setDefaultWorkspace, createWorkspace: createWorkspaceInContext,
    updateWorkspace: updateWorkspaceInContext,
  } = useWorkspace();
  const {
    incompleteSettings,
    loading: customizationLoading,
    getWorkspaceHealth,
  } = useCustomization();
  const isAdmin = ["super_admin", "cxo"].includes(user?.role);
  const canManageUsers = ["super_admin", "cxo", "strategy"].includes(user?.role);

  const [displayName, setDisplayName] = useState(user?.name || "");
  const [savingName, setSavingName] = useState(false);
  const [users, setUsers] = useState([]);
  const [wsRename, setWsRename] = useState(null); // {id, teamCode, clientName, logo, setAsDefault}
  const logoInputRef = useRef(null);
  const [wsDelete, setWsDelete] = useState(null); // {id, name}
  const [wsCreate, setWsCreate] = useState(emptyWorkspaceForm);
  const createLogoInputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [defaultSaving, setDefaultSaving] = useState(null);
  const [workspaceHealth, setWorkspaceHealth] = useState({});

  // user management state
  const EMPTY_USER_FORM = {
    email: "", username: "", name: "", role: "planning", password: "", wsAll: false, wsIds: [],
    ...emptyUserProfile(),
  };
  const [userCreateOpen, setUserCreateOpen] = useState(false);
  const [userForm, setUserForm] = useState(EMPTY_USER_FORM);
  const [userEdit, setUserEdit] = useState(null);
  const [userDelete, setUserDelete] = useState(null); // user object
  const [userReset, setUserReset] = useState(null); // {user, password}
  const [credentialNotice, setCredentialNotice] = useState(null); // {email, username, password}

  const loadUsers = useCallback(() => {
    if (!canManageUsers) return;
    api.get("/users").then((res) => setUsers(res.data)).catch(() => {});
  }, [canManageUsers]);

  useEffect(loadUsers, [loadUsers]);
  useEffect(() => { setDisplayName(user?.name || ""); }, [user?.name]);

  useEffect(() => {
    let cancelled = false;
    if (!workspaces.length) {
      setWorkspaceHealth({});
      return undefined;
    }

    const loadHealth = async () => {
      const results = await Promise.all(workspaces.map(async (workspace) => {
        if (workspace.id === current?.id) {
          return [
            workspace.id,
            customizationLoading ? null : (incompleteSettings || []),
          ];
        }
        try {
          return [workspace.id, await getWorkspaceHealth(workspace.id)];
        } catch {
          // Health is advisory; a failed check must not block workspace
          // administration or imply that the configuration is incomplete.
          return [workspace.id, null];
        }
      }));
      if (!cancelled) setWorkspaceHealth(Object.fromEntries(results));
    };

    loadHealth();
    return () => { cancelled = true; };
  }, [
    workspaces, current?.id, incompleteSettings, customizationLoading, getWorkspaceHealth,
  ]);

  const saveName = async () => {
    if (!displayName.trim()) {
      toast.error("Display name cannot be empty.");
      return;
    }
    setSavingName(true);
    try {
      const res = await api.put("/auth/me", { name: displayName.trim() });
      setUser(res.data);
      toast.success("Profile updated");
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to update profile"));
    } finally {
      setSavingName(false);
    }
  };

  const changeTheme = (t) => {
    setTheme(t);
    toast.success(`Theme set to ${t}`);
  };

  const renameWorkspace = async () => {
    const name = composeWorkspaceName(wsRename?.teamCode, wsRename?.clientName);
    if (!wsRename?.teamCode.trim() || !wsRename?.clientName.trim()) {
      toast.error("Team Code and Client Name are required.");
      return;
    }
    setBusy(true);
    try {
      await updateWorkspaceInContext(
        wsRename.id,
        name,
        wsRename.logo ?? null,
        wsRename.setAsDefault,
      );
      toast.success(`Workspace "${name}" updated`);
      setWsRename(null);
      refreshWorkspaces();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Rename failed"));
    } finally {
      setBusy(false);
    }
  };

  const deleteWorkspace = async () => {
    setBusy(true);
    try {
      await api.delete(`/workspaces/${wsDelete.id}`);
      toast.success(`Workspace "${wsDelete.name}" deleted`);
      if (current.id === wsDelete.id) switchWorkspace("ws_7f2a9c4e1d68");
      setWsDelete(null);
      refreshWorkspaces();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Delete failed"));
    } finally {
      setBusy(false);
    }
  };

  const createWorkspace = async () => {
    const name = composeWorkspaceName(wsCreate.teamCode, wsCreate.clientName);
    if (!wsCreate.teamCode.trim() || !wsCreate.clientName.trim()) {
      toast.error("Team Code and Client Name are required.");
      return;
    }
    setBusy(true);
    try {
      await createWorkspaceInContext(name, wsCreate.logo ?? null, wsCreate.setAsDefault);
      toast.success(`Workspace "${name}" created`);
      setWsCreate(emptyWorkspaceForm());
      refreshWorkspaces();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to create workspace"));
    } finally {
      setBusy(false);
    }
  };

  const changeDefaultWorkspace = async (workspaceId, checked) => {
    setDefaultSaving(workspaceId);
    try {
      await setDefaultWorkspace(checked ? workspaceId : null);
      toast.success(checked ? "Default workspace updated" : "Default workspace cleared");
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to update default workspace"));
    } finally {
      setDefaultSaving(null);
    }
  };

  const changeRole = async (target, role) => {
    try {
      await api.put(`/users/${target.user_id}/role`, { role });
      toast.success(`${target.email} is now ${ROLE_LABELS[role] || role}`);
      loadUsers();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to change role"));
    }
  };

  const setUserWorkspaces = async (target, all, ids) => {
    try {
      await api.put(`/users/${target.user_id}/workspaces`, { all, workspace_ids: ids });
      toast.success(`Workspace access updated for ${target.email}`);
      loadUsers();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to update workspace access"));
      throw e;
    }
  };

  const createUser = async () => {
    const { email, username, name, role, password, wsAll, wsIds, ...profile } = userForm;
    const normalizedPassword = (password || "").trim();
    if (!email.trim() || !username.trim() || !normalizedPassword) {
      toast.error("Email, Emp Code, and a temporary password are required.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/users", {
        email: email.trim(), username: username.trim(), name: name.trim(), role,
        password: normalizedPassword,
        all_workspaces: wsAll, workspace_ids: wsIds,
        ...Object.fromEntries(
          USER_PROFILE_FIELDS.map(({ key }) => [key, (profile[key] || "").trim()])
        ),
      });
      setCredentialNotice({
        email: email.trim(),
        username: username.trim(),
        password: normalizedPassword,
      });
      setUserCreateOpen(false);
      setUserForm(EMPTY_USER_FORM);
      loadUsers();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to create user"));
    } finally {
      setBusy(false);
    }
  };

  const openUserEdit = (target) => {
    setUserEdit({
      user: target,
      name: target.name || "",
      ...userProfileValues(target),
    });
  };

  const saveUserEdit = async () => {
    if (!userEdit?.name.trim()) {
      toast.error("Display name cannot be empty.");
      return;
    }
    setBusy(true);
    try {
      const profile = Object.fromEntries(
        USER_PROFILE_FIELDS.map(({ key }) => [key, (userEdit[key] || "").trim()])
      );
      await api.put(`/users/${userEdit.user.user_id}`, {
        name: userEdit.name.trim(),
        ...profile,
      });
      toast.success(`Details updated for ${userEdit.user.email}.`);
      setUserEdit(null);
      loadUsers();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to update user details"));
    } finally {
      setBusy(false);
    }
  };

  const deleteUser = async () => {
    setBusy(true);
    try {
      await api.delete(`/users/${userDelete.user_id}`);
      toast.success(`${userDelete.email} deleted - access revoked`);
      setUserDelete(null);
      loadUsers();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to delete user"));
    } finally {
      setBusy(false);
    }
  };

  const resetPassword = async () => {
    const normalizedPassword = (userReset?.password || "").trim();
    if (normalizedPassword.length < 6) {
      toast.error("Password must be at least 6 characters.");
      return;
    }
    setBusy(true);
    try {
      await api.put(`/users/${userReset.user.user_id}`, { password: normalizedPassword });
      setCredentialNotice({
        email: userReset.user.email,
        username: userReset.user.username || "",
        password: normalizedPassword,
      });
      setUserReset(null);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to reset password"));
    } finally {
      setBusy(false);
    }
  };

  const initials = (user?.name || user?.email || "U").split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();

  // change own password
  const [pwForm, setPwForm] = useState({ current: "", next: "", confirm: "" });
  const [changingPw, setChangingPw] = useState(false);

  const changeOwnPassword = async () => {
    if (!pwForm.next || pwForm.next.length < 6) {
      toast.error("New password must be at least 6 characters.");
      return;
    }
    if (pwForm.next !== pwForm.confirm) {
      toast.error("New password and confirmation do not match.");
      return;
    }
    setChangingPw(true);
    try {
      await api.post("/auth/change-password", {
        current_password: pwForm.current || null,
        new_password: pwForm.next,
      });
      toast.success("Password changed. Use your new password next time you sign in.");
      setPwForm({ current: "", next: "", confirm: "" });
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to change password"));
    } finally {
      setChangingPw(false);
    }
  };

  return (
    <div className="max-w-3xl space-y-5" data-testid="settings-page">
      {/* Profile */}
      <SectionCard title="Profile" subtitle="Your account details" testId="settings-profile-card">
        <div className="flex items-center gap-4 mb-5">
          <Avatar className="h-14 w-14">
            <AvatarImage src={user?.picture} alt={user?.name} />
            <AvatarFallback className="bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-200 text-lg font-semibold">{initials}</AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-foreground truncate">{user?.name || "User"}</p>
            <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
            <Badge className={`mt-1.5 rounded-full text-[10px] px-2 border ${
              isAdmin
                ? "bg-teal-50 text-teal-800 border-teal-200 dark:bg-teal-950/50 dark:text-teal-300 dark:border-teal-900 hover:bg-teal-50"
                : "bg-muted text-muted-foreground hover:bg-muted"
            }`} data-testid="settings-role-badge">
              {isAdmin ? <ShieldCheck className="h-3 w-3 mr-1" /> : null}
              {ROLE_LABELS[user?.role] || "Planning"}
            </Badge>
          </div>
        </div>
        <div className="flex items-end gap-2 max-w-md">
          <div className="flex-1 space-y-1.5">
            <Label>Display name</Label>
            <Input data-testid="settings-display-name-input" value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && saveName()} />
          </div>
          <Button data-testid="settings-save-name-button" onClick={saveName} disabled={savingName}>
            {savingName ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
          </Button>
        </div>
        <div className="mt-6 pt-5 border-t max-w-md">
          <p className="text-sm font-semibold text-foreground mb-1">Change password</p>
          <p className="text-xs text-muted-foreground mb-3">
            Signing out of your other devices happens automatically after a password change.
          </p>
          <div className="space-y-2.5">
            <div className="space-y-1.5">
              <Label>Current password</Label>
              <Input type="password" data-testid="settings-current-password-input"
                placeholder="Leave blank if you never set one"
                value={pwForm.current} onChange={(e) => setPwForm({ ...pwForm, current: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>New password</Label>
              <Input type="password" data-testid="settings-new-password-input" placeholder="Min. 6 characters"
                value={pwForm.next} onChange={(e) => setPwForm({ ...pwForm, next: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Confirm new password</Label>
              <Input type="password" data-testid="settings-confirm-password-input" placeholder="Repeat new password"
                value={pwForm.confirm} onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })}
                onKeyDown={(e) => e.key === "Enter" && changeOwnPassword()} />
            </div>
            <Button data-testid="settings-change-password-button" onClick={changeOwnPassword}
              disabled={changingPw} variant="outline" className="mt-1">
              {changingPw ? <Loader2 className="h-4 w-4 animate-spin" /> : <><KeyRound className="h-3.5 w-3.5 mr-1.5" /> Change Password</>}
            </Button>
          </div>
        </div>
      </SectionCard>

      {/* Appearance */}
      <SectionCard
        title="Appearance"
        subtitle={<>Choose how <strong>Global CRT (Client Revenue Tracker)</strong> looks for you</>}
        testId="settings-appearance-card"
      >
        <div className="grid grid-cols-3 gap-3 max-w-md">
          {THEME_OPTIONS.map(({ value, label, icon: Icon }) => (
            <button
              key={value}
              data-testid={`theme-option-${value}`}
              onClick={() => changeTheme(value)}
              className={`flex flex-col items-center gap-2 rounded-xl border p-4 text-sm font-medium transition-[border-color,background-color] duration-150 ${
                theme === value
                  ? "border-[hsl(var(--primary))] bg-teal-50 dark:bg-teal-950/40 text-foreground ring-1 ring-[hsl(var(--primary))]"
                  : "text-muted-foreground hover:bg-muted/60"
              }`}
            >
              <Icon className="h-5 w-5" />
              {label}
            </button>
          ))}
        </div>
      </SectionCard>

      {/* Per-user workspace preference and, for managers, workspace management */}
      <SectionCard
        title="Workspaces"
        subtitle={isAdmin
          ? "Choose your default workspace or manage workspaces. Deleting removes all of its data."
          : "Choose which workspace opens by default for you. This does not change your access."}
        testId="settings-workspaces-card"
      >
          {isAdmin && (
            <div className="flex justify-end mb-3">
              <Button size="sm" className="rounded-lg" data-testid="workspace-create-button"
                onClick={() => setWsCreate({ ...emptyWorkspaceForm(), open: true })}>
                <Plus className="h-4 w-4 mr-1.5" /> Add Workspace
              </Button>
            </div>
          )}
          <div className="space-y-2">
            {workspaces.map((w) => (
              <div key={w.id} data-testid={`settings-workspace-row-${w.id}`}
                className="flex items-center gap-3 rounded-lg border px-3 py-2.5">
                {w.logo ? (
                  <img src={w.logo} alt={w.name}
                    className="h-8 w-8 rounded-md object-contain bg-white border border-border shrink-0" />
                ) : (
                  <div className="h-8 w-8 rounded-md bg-teal-50 border border-teal-100 dark:bg-teal-950/50 dark:border-teal-900 flex items-center justify-center text-[10px] font-bold text-teal-700 dark:text-teal-300 shrink-0">
                    {w.name.slice(0, 2).toUpperCase()}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">{w.name}</p>
                   {defaultWorkspaceId === w.id && (
                     <p className="text-[10px] text-teal-700 dark:text-teal-300 uppercase tracking-wide">Your default workspace</p>
                   )}
                   {workspaceHealth[w.id]?.length > 0 && (
                     <div
                       className="mt-1 flex items-start gap-1.5 text-[11px] leading-4 text-amber-800 dark:text-amber-300"
                       data-testid={`workspace-customization-health-${w.id}`}
                       role="status"
                       title={`Needs attention: ${workspaceHealth[w.id].map((issue) => issue.label).join(", ")}`}
                     >
                       <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                       <span>
                         <span className="font-medium">Needs attention:</span>{" "}
                         {workspaceHealth[w.id].map((issue) => issue.label).join(", ")}
                       </span>
                     </div>
                   )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Label htmlFor={`workspace-default-${w.id}`} className="text-xs text-muted-foreground cursor-pointer">
                    Default
                  </Label>
                  <Switch
                    id={`workspace-default-${w.id}`}
                    checked={defaultWorkspaceId === w.id}
                    disabled={defaultSaving !== null}
                    onCheckedChange={(checked) => changeDefaultWorkspace(w.id, checked)}
                    data-testid={`workspace-default-toggle-${w.id}`}
                  />
                </div>
                {isAdmin && (
                  <>
                    <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`workspace-rename-${w.id}`}
                      title="Edit workspace" onClick={() => setWsRename({
                        id: w.id, ...splitWorkspaceName(w.name), logo: w.logo || null,
                        setAsDefault: defaultWorkspaceId === w.id,
                      })}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button variant="ghost" size="icon" disabled={w.is_default}
                      className="h-8 w-8 text-red-500 hover:text-red-600 disabled:opacity-30"
                      data-testid={`workspace-delete-${w.id}`}
                      title={w.is_default ? "The standard service catalog workspace cannot be deleted" : "Delete workspace"}
                      onClick={() => setWsDelete({ id: w.id, name: w.name })}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </>
                )}
              </div>
            ))}
          </div>
      </SectionCard>

      {/* Users below the current manager's access level */}
      {canManageUsers && (
        <SectionCard title="Users & Privileges"
          subtitle={user?.role === "super_admin"
            ? "View all accounts. Edit, reset, or delete other Super Admin accounts; their role and workspace access remain protected."
            : "Create accounts, manage users below your access level, reset passwords, or revoke access."}
          testId="settings-users-card">
          <div className="flex flex-wrap justify-between gap-2 mb-3">
            <ImportDialog entity="users" entityLabel="Users" onImported={loadUsers} testIdPrefix="user" />
            <Button size="sm" className="rounded-lg" data-testid="user-create-button"
              onClick={() => { setUserForm({ ...EMPTY_USER_FORM, password: genPassword() }); setUserCreateOpen(true); }}>
              <UserPlus className="h-4 w-4 mr-1.5" /> Add User
            </Button>
          </div>
          <div className="space-y-2">
            {users.map((u) => {
              const isSelf = u.user_id === user?.user_id;
              const isSuperAdminRow = u.locked || u.role === "super_admin";
              return (
                <div key={u.user_id} data-testid={`settings-user-row-${u.user_id}`}
                  className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border px-3 py-2.5">
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={u.picture} alt={u.name} />
                    <AvatarFallback className="bg-muted text-muted-foreground text-xs font-semibold">
                      {(u.name || u.email || "U").slice(0, 2).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-[150px]">
                    <p className="text-sm font-medium text-foreground truncate">
                      {u.name || u.email}
                      {isSelf && <span className="ml-1.5 text-[10px] text-muted-foreground uppercase tracking-wide">(you)</span>}
                    </p>
                    <p className="text-xs text-muted-foreground truncate" data-testid={`user-username-${u.user_id}`}>
                      Emp Code: {u.username ? u.username.toUpperCase() : "Not set"}
                    </p>
                    <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                  </div>
                  <Button variant="ghost" size="icon" className="h-8 w-8"
                    data-testid={`user-edit-${u.user_id}`}
                    title="Edit user details"
                    onClick={() => openUserEdit(u)}>
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  {isSuperAdminRow ? (
                    <>
                      <Badge className="rounded-full text-[10px] px-2 bg-teal-50 text-teal-800 border border-teal-200 dark:bg-teal-950/50 dark:text-teal-300 dark:border-teal-900 hover:bg-teal-50"
                        data-testid={`user-locked-badge-${u.user_id}`}>
                        <Lock className="h-3 w-3 mr-1" /> Super Admin
                      </Badge>
                      {!isSelf && user?.role === "super_admin" && (
                        <>
                          <Button variant="ghost" size="icon" className="h-8 w-8"
                            data-testid={`user-reset-password-${u.user_id}`} title="Reset password"
                            onClick={() => setUserReset({ user: u, password: genPassword() })}>
                            <KeyRound className="h-3.5 w-3.5" />
                          </Button>
                          <Button variant="ghost" size="icon"
                            className="h-8 w-8 text-red-500 hover:text-red-600"
                            data-testid={`user-delete-${u.user_id}`}
                            title="Delete user (revokes access)"
                            onClick={() => setUserDelete(u)}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </>
                      )}
                    </>
                  ) : (
                    <>
                      <Select value={u.role} onValueChange={(role) => changeRole(u, role)} disabled={isSelf}>
                        <SelectTrigger className="w-[110px] h-8 text-xs rounded-lg" data-testid={`user-role-select-${u.user_id}`}
                          title={isSelf ? "You cannot change your own access level" : "Change access level"}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {rolesBelow(user?.role).map((role) => (
                            <SelectItem key={role} value={role}>{ROLE_LABELS[role]}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <WsAccessPicker workspaces={workspaces} value={u.workspace_ids}
                        disabled={isSelf && user?.role !== "super_admin"}
                        testId={`user-ws-access-${u.user_id}`}
                        onApply={(all, ids) => setUserWorkspaces(u, all, ids)} />
                      <Button variant="ghost" size="icon" className="h-8 w-8"
                        data-testid={`user-reset-password-${u.user_id}`} title="Reset password"
                        onClick={() => setUserReset({ user: u, password: genPassword() })}>
                        <KeyRound className="h-3.5 w-3.5" />
                      </Button>
                      <Button variant="ghost" size="icon" disabled={isSelf}
                        className="h-8 w-8 text-red-500 hover:text-red-600 disabled:opacity-30"
                        data-testid={`user-delete-${u.user_id}`}
                        title={isSelf ? "You cannot delete your own account" : "Delete user (revokes access)"}
                        onClick={() => setUserDelete(u)}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </SectionCard>
      )}

      {/* Create workspace dialog */}
      <Dialog open={wsCreate.open} onOpenChange={(o) => !o && setWsCreate(emptyWorkspaceForm())}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Add Workspace</DialogTitle>
            <DialogDescription>Create a new workspace. Services from the default workspace will be copied in.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Logo upload */}
            <div className="space-y-1.5">
              <Label>Logo <span className="text-muted-foreground font-normal">(optional)</span></Label>
              <div className="flex items-center gap-3">
                {wsCreate.logo ? (
                  <div className="relative h-14 w-14 shrink-0">
                    <img src={wsCreate.logo} alt="workspace logo preview"
                      className="h-14 w-14 rounded-lg object-contain bg-white border border-border" />
                    <button type="button"
                      onClick={() => setWsCreate({ ...wsCreate, logo: null })}
                      className="absolute -top-1.5 -right-1.5 h-4 w-4 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center hover:opacity-90"
                      aria-label="Remove logo">
                      <X className="h-2.5 w-2.5" />
                    </button>
                  </div>
                ) : (
                  <div className="h-14 w-14 rounded-lg border-2 border-dashed border-border flex items-center justify-center text-muted-foreground shrink-0">
                    <Upload className="h-5 w-5" />
                  </div>
                )}
                <div className="flex flex-col gap-1.5 min-w-0">
                  <input ref={createLogoInputRef} type="file" accept="image/*" className="hidden"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      if (!isValidLogoFile(file, toast)) { e.target.value = ""; return; }
                      setWsCreate({ ...wsCreate, logo: await toBase64(file) });
                      e.target.value = "";
                    }} />
                  <Button type="button" variant="outline" size="sm" className="w-fit"
                    onClick={() => createLogoInputRef.current?.click()}>
                    <Upload className="h-3.5 w-3.5 mr-1.5" />
                    {wsCreate.logo ? "Change logo" : "Upload logo"}
                  </Button>
                  <p className="text-xs text-muted-foreground">PNG, JPG or SVG · max 1 MB</p>
                </div>
              </div>
            </div>
            {/* Workspace name parts */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="workspace-create-team-code">Team Code</Label>
                <Input
                  id="workspace-create-team-code"
                  data-testid="workspace-create-team-code-input"
                  placeholder="e.g. APAC"
                  value={wsCreate.teamCode}
                  onChange={(e) => setWsCreate({
                    ...wsCreate, teamCode: normalizeWorkspacePart(e.target.value),
                  })}
                  onKeyDown={(e) => e.key === "Enter" && createWorkspace()}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="workspace-create-client-name">Client Name</Label>
                <Input
                  id="workspace-create-client-name"
                  data-testid="workspace-create-client-name-input"
                  placeholder="e.g. ACME"
                  value={wsCreate.clientName}
                  onChange={(e) => setWsCreate({
                    ...wsCreate, clientName: normalizeWorkspacePart(e.target.value),
                  })}
                  onKeyDown={(e) => e.key === "Enter" && createWorkspace()}
                />
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              Workspace name: <span className="font-medium text-foreground">
                {composeWorkspaceName(wsCreate.teamCode, wsCreate.clientName) || "TEAM CODE - CLIENT NAME"}
              </span>
            </p>
            <label className="flex items-center justify-between gap-3 rounded-lg border px-3 py-2.5 cursor-pointer">
              <div>
                <p className="text-sm font-medium">Set as my default workspace</p>
                <p className="text-[11px] text-muted-foreground">This changes the workspace that opens for you after sign-in.</p>
              </div>
              <Switch checked={wsCreate.setAsDefault}
                onCheckedChange={(checked) => setWsCreate({ ...wsCreate, setAsDefault: checked })}
                data-testid="workspace-create-default-toggle" />
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setWsCreate(emptyWorkspaceForm())}>Cancel</Button>
            <Button data-testid="workspace-create-submit" onClick={createWorkspace} disabled={busy}>
              {busy ? "Creating..." : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit workspace dialog (rename + logo) */}
      <Dialog open={!!wsRename} onOpenChange={(o) => !o && setWsRename(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Edit Workspace</DialogTitle>
            <DialogDescription>Update the name and logo for this workspace.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Logo upload */}
            <div className="space-y-1.5">
              <Label>Logo <span className="text-muted-foreground font-normal">(optional)</span></Label>
              <div className="flex items-center gap-3">
                {wsRename?.logo ? (
                  <div className="relative h-14 w-14 shrink-0">
                    <img src={wsRename.logo} alt="workspace logo preview"
                      className="h-14 w-14 rounded-lg object-contain bg-white border border-border" />
                    <button type="button"
                      onClick={() => setWsRename({ ...wsRename, logo: null })}
                      className="absolute -top-1.5 -right-1.5 h-4 w-4 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center hover:opacity-90"
                      aria-label="Remove logo">
                      <X className="h-2.5 w-2.5" />
                    </button>
                  </div>
                ) : (
                  <div className="h-14 w-14 rounded-lg border-2 border-dashed border-border flex items-center justify-center text-muted-foreground shrink-0">
                    <Upload className="h-5 w-5" />
                  </div>
                )}
                <div className="flex flex-col gap-1.5 min-w-0">
                  <input ref={logoInputRef} type="file" accept="image/*" className="hidden"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      if (!isValidLogoFile(file, toast)) { e.target.value = ""; return; }
                      setWsRename({ ...wsRename, logo: await toBase64(file) });
                      e.target.value = "";
                    }} />
                  <Button type="button" variant="outline" size="sm" className="w-fit"
                    onClick={() => logoInputRef.current?.click()}>
                    <Upload className="h-3.5 w-3.5 mr-1.5" />
                    {wsRename?.logo ? "Change logo" : "Upload logo"}
                  </Button>
                  <p className="text-xs text-muted-foreground">PNG, JPG or SVG · max 1 MB</p>
                </div>
              </div>
            </div>
            {/* Workspace name parts */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="workspace-rename-team-code">Team Code</Label>
                <Input
                  id="workspace-rename-team-code"
                  data-testid="workspace-rename-team-code-input"
                  placeholder="e.g. APAC"
                  value={wsRename?.teamCode || ""}
                  onChange={(e) => setWsRename({
                    ...wsRename, teamCode: normalizeWorkspacePart(e.target.value),
                  })}
                  onKeyDown={(e) => e.key === "Enter" && renameWorkspace()}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="workspace-rename-client-name">Client Name</Label>
                <Input
                  id="workspace-rename-client-name"
                  data-testid="workspace-rename-client-name-input"
                  placeholder="e.g. ACME"
                  value={wsRename?.clientName || ""}
                  onChange={(e) => setWsRename({
                    ...wsRename, clientName: normalizeWorkspacePart(e.target.value),
                  })}
                  onKeyDown={(e) => e.key === "Enter" && renameWorkspace()}
                />
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              Workspace name: <span className="font-medium text-foreground">
                {composeWorkspaceName(wsRename?.teamCode, wsRename?.clientName) || "TEAM CODE - CLIENT NAME"}
              </span>
            </p>
            <label className="flex items-center justify-between gap-3 rounded-lg border px-3 py-2.5 cursor-pointer">
              <div>
                <p className="text-sm font-medium">Set as my default workspace</p>
                <p className="text-[11px] text-muted-foreground">Only one workspace can be your default at a time.</p>
              </div>
              <Switch checked={Boolean(wsRename?.setAsDefault)}
                onCheckedChange={(checked) => setWsRename({ ...wsRename, setAsDefault: checked })}
                data-testid="workspace-edit-default-toggle" />
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setWsRename(null)}>Cancel</Button>
            <Button data-testid="workspace-rename-submit" onClick={renameWorkspace} disabled={busy}>
              {busy ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Show the exact password sent to the server once, so it can be copied reliably. */}
      <Dialog open={!!credentialNotice} onOpenChange={(open) => !open && setCredentialNotice(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Temporary password ready</DialogTitle>
            <DialogDescription>
              Share this password securely. It is shown only in this confirmation.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="rounded-lg border bg-muted/40 p-3 text-sm">
              <p><span className="text-muted-foreground">Emp Code:</span> {credentialNotice?.username || "—"}</p>
              <p><span className="text-muted-foreground">Email:</span> {credentialNotice?.email}</p>
            </div>
            <div className="flex gap-1.5">
              <Input readOnly type="text" className="font-mono text-sm"
                value={credentialNotice?.password || ""} data-testid="credential-notice-password" />
              <Button variant="outline" size="icon" title="Copy password"
                data-testid="credential-notice-password-copy"
                onClick={() => copyText(credentialNotice?.password || "", "Password")}>
                <Copy className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="credential-notice-close" onClick={() => setCredentialNotice(null)}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!wsDelete} onOpenChange={(o) => !o && setWsDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete workspace "{wsDelete?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently deletes ALL data in this workspace - revenue lines, engagements,
              services, business days, overrides, and presets. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="workspace-delete-confirm" onClick={deleteWorkspace}
              className="bg-red-600 hover:bg-red-700">
              Delete Workspace
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {/* Create user dialog */}
      <Dialog open={userCreateOpen} onOpenChange={(o) => !o && setUserCreateOpen(false)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add User</DialogTitle>
            <DialogDescription>
              Create an account and share the temporary password with the user. They can sign in immediately.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {/* Emp Code + Email */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div className="space-y-1.5">
                <Label>Emp Code *</Label>
                <Input data-testid="user-create-username-input" placeholder="Ex: GA00000"
                  value={userForm.username}
                  onChange={(e) => setUserForm({ ...userForm, username: e.target.value.replace(/[^a-zA-Z0-9]/g, "") })} />
              </div>
              <div className="space-y-1.5">
                <Label>Email *</Label>
                <Input type="email" data-testid="user-create-email-input" placeholder="Ex: user@globalhealthcareresource.com"
                  value={userForm.email} onChange={(e) => setUserForm({ ...userForm, email: e.target.value })} />
              </div>
            </div>

            <p className="text-[11px] text-muted-foreground -mt-1">
              Use letters and numbers only. This code is reserved for future HRMS mapping.
            </p>

            {/* Display name */}
            <div className="space-y-1.5">
              <Label>Display name</Label>
              <Input data-testid="user-create-name-input" placeholder="Full name"
                value={userForm.name} onChange={(e) => setUserForm({ ...userForm, name: e.target.value })} />
            </div>

            <div className="border-t pt-3">
              <p className="text-xs font-medium text-foreground mb-2">
                HRMS details <span className="font-normal text-muted-foreground">(optional)</span>
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                {USER_PROFILE_FIELDS.map(({ key, label, placeholder }) => (
                  <div className="space-y-1.5" key={key}>
                    <Label>{label}</Label>
                    <Input data-testid={`user-create-${key.replace(/_/g, "-")}-input`} placeholder={placeholder}
                      value={userForm[key]}
                      onChange={(e) => setUserForm({ ...userForm, [key]: e.target.value })} />
                  </div>
                ))}
              </div>
            </div>

            {/* Role — short names in select, live description below */}
            <div className="space-y-1.5">
              <Label>Access level</Label>
              <Select value={userForm.role} onValueChange={(role) => {
                const getsAllWs = ALL_WS_ROLES.includes(role);
                setUserForm({ ...userForm, role, wsAll: getsAllWs, wsIds: getsAllWs ? [] : userForm.wsIds });
              }}>
                <SelectTrigger data-testid="user-create-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {user?.role === "super_admin" && (
                    <SelectItem value="super_admin">Super Admin</SelectItem>
                  )}
                  {rolesBelow(user?.role).map((role) => (
                    <SelectItem key={role} value={role}>{ROLE_LABELS[role]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {ROLE_DESCRIPTIONS[userForm.role] && (
                <p className="text-[11px] text-muted-foreground leading-relaxed">
                  {ROLE_DESCRIPTIONS[userForm.role]}
                </p>
              )}
            </div>

            {/* Workspace access — hidden for roles that auto-get all workspaces */}
            {ALL_WS_ROLES.includes(userForm.role) ? (
              <div className="flex items-center gap-2 rounded-lg bg-muted/60 border px-3 py-2 text-[11px] text-muted-foreground">
                <Building2 className="h-3.5 w-3.5 shrink-0 text-teal-600 dark:text-teal-400" />
                <span>Automatically assigned to <strong className="text-foreground font-medium">all workspaces</strong>.</span>
              </div>
            ) : (
              <div className="space-y-1.5">
                <Label>Workspace access</Label>
                <WsAccessPicker workspaces={workspaces}
                  value={userForm.wsAll ? null : userForm.wsIds}
                  testId="user-create-ws-access"
                  onApply={async (all, ids) => setUserForm((f) => ({ ...f, wsAll: all, wsIds: ids }))} />
                <p className="text-[11px] text-muted-foreground">
                  Default: no access. Assign at least one workspace so the user can sign in.
                </p>
              </div>
            )}

            {/* Temporary password */}
            <div className="space-y-1.5">
              <Label>Temporary password *</Label>
              <div className="flex gap-1.5">
                <Input data-testid="user-create-password-input" className="font-mono text-sm"
                  type="text" autoComplete="new-password" spellCheck={false}
                  autoCapitalize="none" autoCorrect="off"
                  value={userForm.password} onChange={(e) => setUserForm({ ...userForm, password: e.target.value })} />
                <Button variant="outline" size="icon" className="shrink-0" title="Generate a new password"
                  data-testid="user-create-password-generate"
                  onClick={() => setUserForm({ ...userForm, password: genPassword() })}>
                  <RefreshCw className="h-3.5 w-3.5" />
                </Button>
                <Button variant="outline" size="icon" className="shrink-0" title="Copy password"
                  data-testid="user-create-password-copy"
                  onClick={() => copyText(userForm.password, "Password")}>
                  <Copy className="h-3.5 w-3.5" />
                </Button>
              </div>
              <p className="text-[11px] text-muted-foreground">Minimum 6 characters. Share it securely with the user.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUserCreateOpen(false)}>Cancel</Button>
            <Button data-testid="user-create-submit" onClick={createUser} disabled={busy}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create User"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit user details dialog */}
      <Dialog open={!!userEdit} onOpenChange={(o) => !o && setUserEdit(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit User Details</DialogTitle>
            <DialogDescription>
              Maintain HRMS-ready employee information for {userEdit?.user?.email}. These fields are optional until HRMS integration is connected.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="space-y-1.5 md:col-span-2">
              <Label>Display name</Label>
              <Input data-testid="user-edit-name-input" value={userEdit?.name || ""}
                onChange={(e) => setUserEdit({ ...userEdit, name: e.target.value })} />
            </div>
            {USER_PROFILE_FIELDS.map(({ key, label, placeholder }) => (
              <div className="space-y-1.5" key={key}>
                <Label>{label}</Label>
                <Input data-testid={`user-edit-${key.replace(/_/g, "-")}-input`} placeholder={placeholder}
                  value={userEdit?.[key] || ""}
                  onChange={(e) => setUserEdit({ ...userEdit, [key]: e.target.value })} />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUserEdit(null)}>Cancel</Button>
            <Button data-testid="user-edit-submit" onClick={saveUserEdit} disabled={busy}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Details"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset password dialog */}
      <Dialog open={!!userReset} onOpenChange={(o) => !o && setUserReset(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
            <DialogDescription>
              Set a new temporary password for {userReset?.user?.email}. Their current sessions will be signed out.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label>New temporary password</Label>
            <div className="flex gap-1.5">
              <Input data-testid="user-reset-password-input" className="font-mono text-sm"
                type="text" autoComplete="new-password" spellCheck={false}
                autoCapitalize="none" autoCorrect="off"
                value={userReset?.password || ""}
                onChange={(e) => setUserReset({ ...userReset, password: e.target.value })} />
              <Button variant="outline" size="icon" className="shrink-0" title="Generate a new password"
                onClick={() => setUserReset({ ...userReset, password: genPassword() })}>
                <RefreshCw className="h-3.5 w-3.5" />
              </Button>
              <Button variant="outline" size="icon" className="shrink-0" title="Copy password"
                onClick={() => copyText(userReset?.password || "", "Password")}>
                <Copy className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUserReset(null)}>Cancel</Button>
            <Button data-testid="user-reset-password-submit" onClick={resetPassword} disabled={busy}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Reset Password"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete user confirm */}
      <AlertDialog open={!!userDelete} onOpenChange={(o) => !o && setUserDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete user {userDelete?.email}?</AlertDialogTitle>
            <AlertDialogDescription>
              This revokes their access immediately - they will no longer be able to sign in.
              Their saved filter presets are also removed. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="user-delete-confirm" onClick={deleteUser}
              className="bg-red-600 hover:bg-red-700">
              Delete User
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
